import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import UsagePill from "@/components/UsagePill";
import { getFounderId } from "@/lib/founder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOADING_STAGES = [
  "Reading your idea",
  "Pulling live market signals",
  "Reading your idea-strength signal",
  "Mapping your ideal buyer",
  "Scoring target markets",
  "Benchmarking pricing",
  "Finding your wedge",
];

export default function Analyze() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    idea: "",
    problem: "",
    country: "",
    target_market: "",
    business_type: "unsure",
  });
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.idea.trim() || !form.problem.trim() || !form.country.trim()) {
      toast.error("Idea, problem, and country are required.");
      return;
    }
    setLoading(true);
    setStage(0);
    const stageTimer = setInterval(() => {
      setStage((s) => (s < LOADING_STAGES.length - 1 ? s + 1 : s));
    }, 4200);

    try {
      const payload = { ...form, founder_id: getFounderId() };
      const { data } = await axios.post(`${API}/analyze`, payload, { timeout: 180000 });
      clearInterval(stageTimer);
      // stash on session so ReportPage can render without another fetch
      sessionStorage.setItem(`velo21:${data.id}`, JSON.stringify(data));
      nav(`/r/${data.id}`);
    } catch (err) {
      clearInterval(stageTimer);
      setLoading(false);
      if (err?.response?.status === 402) {
        toast.error("You've reached your validation quota on this browser.");
        nav("/upgrade");
        return;
      }
      const msg = err?.response?.data?.detail || err.message || "Something went wrong.";
      toast.error(`Analysis failed: ${typeof msg === "string" ? msg : JSON.stringify(msg)}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 grain-bg" data-testid="analyze-loading">
        <div className="max-w-md w-full velo-card p-10 relative z-10">
          <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden mb-8">
            <div className="h-full velo-progress rounded-full" style={{ width: "100%" }} />
          </div>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Working</p>
          <h2 className="font-serif-display text-3xl leading-tight text-zinc-900 mb-6" data-testid="loading-stage-text">
            {LOADING_STAGES[stage]}
            <span className="velo-dot">.</span>
            <span className="velo-dot">.</span>
            <span className="velo-dot">.</span>
          </h2>
          <ul className="space-y-2">
            {LOADING_STAGES.map((s, i) => (
              <li key={s} className={`text-sm flex items-center gap-2 ${i <= stage ? "text-zinc-900" : "text-zinc-400"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${i <= stage ? "bg-[#FF4757]" : "bg-zinc-300"}`} />
                {s}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-zinc-500 leading-relaxed">
            Grounding the analysis with live web signals. Typically 30–60 seconds — occasionally longer for niche ideas.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain-bg" data-testid="analyze-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-zinc-200/60">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-bold tracking-tight">velo2one</span>
          </Link>
          <div className="flex items-center gap-3">
            <UsagePill />
            <span className="hidden sm:inline text-xs text-zinc-500">Step 1 of 1 &mdash; Intake</span>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-3xl mx-auto px-6 md:px-8 py-14">
        <h1 className="font-serif-display text-5xl md:text-6xl text-zinc-900 leading-[1.02] mb-3">
          Tell us about your idea.
        </h1>
        <p className="text-zinc-600 text-base mb-10 max-w-xl leading-relaxed">
          You&rsquo;ll get your idea-strength signal <em>and</em> the full ICP, market, pricing and competition
          plan &mdash; either way. Two sentences beats two paragraphs.
        </p>

        <form onSubmit={submit} className="space-y-8" data-testid="intake-form">
          <Field label="Describe your idea in a sentence or two." required>
            <textarea
              data-testid="input-idea"
              value={form.idea}
              onChange={upd("idea")}
              rows={3}
              placeholder="e.g. An app that helps small solo therapists automate insurance claims."
              className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-4 focus:ring-[#FF4757]/25 focus:border-[#FF4757] transition-shadow"
            />
          </Field>

          <Field label="What problem does it solve, and for whom?" required>
            <textarea
              data-testid="input-problem"
              value={form.problem}
              onChange={upd("problem")}
              rows={3}
              placeholder="e.g. Solo therapists lose 8+ hours a week on claim paperwork — no affordable tool exists for solo practices."
              className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-4 focus:ring-[#FF4757]/25 focus:border-[#FF4757] transition-shadow"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="What country are you building from?" required>
              <input
                data-testid="input-country"
                value={form.country}
                onChange={upd("country")}
                placeholder="e.g. United States"
                className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-4 focus:ring-[#FF4757]/25 focus:border-[#FF4757] transition-shadow"
              />
            </Field>

            <Field label="Target market (optional)">
              <input
                data-testid="input-target-market"
                value={form.target_market}
                onChange={upd("target_market")}
                placeholder="e.g. United States, or United Kingdom"
                className="w-full rounded-2xl border border-zinc-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-4 focus:ring-[#FF4757]/25 focus:border-[#FF4757] transition-shadow"
              />
            </Field>
          </div>

          <Field label="Business type">
            <p className="text-xs text-zinc-500 -mt-1 mb-3">Not sure? Pick that &mdash; we&rsquo;ll infer and let you confirm.</p>
            <div className="flex flex-wrap gap-2" data-testid="business-type-group">
              {[
                { v: "B2C", l: "B2C" },
                { v: "B2B", l: "B2B" },
                { v: "B2G", l: "B2G" },
                { v: "unsure", l: "Not sure" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`btype-${opt.v}`}
                  onClick={() => setForm({ ...form, business_type: opt.v })}
                  className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors ${
                    form.business_type === opt.v
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </Field>

          <div className="pt-4">
            <button
              type="submit"
              data-testid="submit-analysis-btn"
              className="btn-primary rounded-full px-7 py-3.5 text-base font-semibold inline-flex items-center gap-2"
            >
              Run my full plan
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            </button>
            <p className="text-xs text-zinc-500 mt-3">Takes ~30–60 seconds. Your report gets a shareable link.</p>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-zinc-900 mb-2">
        {label}
        {required && <span className="text-[#FF4757] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
